import {Place} from '@wix/ambassador-seating-v1-seating-plan/types'
import {focusElement, hookToAttributeSelector} from '@wix/panda-js-utils'
import {BI_ORIGINS, PlaceWithTicketInfo, getEventTitle, getFormattedLocation} from '@wix/wix-events-commons-statics'
import {detailsPageDataHooks as DH} from '@wix/wix-events-data-hooks'
import {useEnvironment} from '@wix/yoshi-flow-editor'
import classNames from 'classnames'
import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import {useEventDateInformation} from '../../../../../../commons/hooks/dates'
import {useAppActions, useAppSelector} from '../../../hooks/runtime'
import {getEvent} from '../../../selectors/event'
import {getReservationError} from '../../../selectors/reservation'
import {getFirstPlaceWithDonationError, getSeatingError} from '../../../selectors/seating/errors'
import {getShowPricingOptionsPlace, isInAccessibilityMode} from '../../../selectors/seating/mode'
import {isPlaceArea, isPlaceReservableAsWhole} from '../../../selectors/seating/place'
import {calculatePlacesStock, getPlaces, getPlacesInBasket, getSelectedPlace} from '../../../selectors/seating/places'
import {isLoadingSeating} from '../../../selectors/seating/seating'
import {FormEntry} from '../../chunks/form-entry'
import {Modal} from '../modal'
import {Desktop} from './desktop'
import {ErrorModal} from './error-modal'
import {Loader} from './loader'
import {Mobile} from './mobile'
import {PricingOptionModal} from './pricing-options-modals/pricing-option-modal'
import {TablePricingOptionsModal} from './pricing-options-modals/table-pricing-options-modal'
import {classes} from './seating-plan.st.css'

export const SeatingPlan = () => {
  const {isMobile: mobile} = useEnvironment()
  const desktop = !mobile
  const focusErrorRequest = useRef<boolean>(false)
  const {setPlaceQuantity, unselectPlace, setShowPlaceDescription, clickPlace, seatingCheckout} = useAppActions()

  const event = useAppSelector(getEvent)
  const loading = useAppSelector(isLoadingSeating)
  const seatingError = useAppSelector(getSeatingError)
  const reservationError = useAppSelector(getReservationError)
  const placesWithTicketInfo = useAppSelector(getPlaces)
  const placesInBasket = useAppSelector(getPlacesInBasket)
  const showAccessibilityMode = useAppSelector(isInAccessibilityMode)
  const selectedPlace = useAppSelector(getSelectedPlace)
  const showPriceOptionsPlace = useAppSelector(getShowPricingOptionsPlace)
  const firstPlaceWithError = useAppSelector(getFirstPlaceWithDonationError)

  const {shortStartDate} = useEventDateInformation(event.id)

  useEffect(() => {
    document.querySelector('#PAGES_CONTAINER')?.classList.add('ignore-focus')

    return () => document.querySelector('#PAGES_CONTAINER')?.classList.remove('ignore-focus')
  }, [placesInBasket])

  useEffect(() => {
    if (focusErrorRequest.current) {
      focusErrorRequest.current = false

      if (firstPlaceWithError?.id) {
        focusElement({
          selector: `${hookToAttributeSelector(`ticket-${firstPlaceWithError.id}`)} ${hookToAttributeSelector(
            DH.DONATION_INPUT,
          )}`,
        })
      }
    }
  })

  const handleCheckout = useCallback(() => {
    FormEntry.preload()
    focusErrorRequest.current = true
    seatingCheckout()
  }, [seatingCheckout])

  const onRemoveClick = useCallback((place: PlaceWithTicketInfo) => {
    setPlaceQuantity({
      placeId: place.id,
      count: 0,
      origin: BI_ORIGINS.SEATINGS_MODAL_BASKET,
    })
  }, [])

  const onCloseClick = useCallback(() => {
    if (selectedPlace?.id) {
      unselectPlace()
    }
  }, [selectedPlace])

  const placesStock = useMemo(() => calculatePlacesStock(placesWithTicketInfo), [placesWithTicketInfo])
  const selectedPlacesIds = useMemo(() => placesInBasket.map(place => place.id), [placesInBasket])

  const onPlaceClick = useCallback(
    ({place}: {place?: Place}) => {
      const occupied = placesStock[place.id] === 0
      const selected = selectedPlacesIds.includes(place.id)

      if (occupied && (!selected || isPlaceArea(place))) {
        return
      }

      clickPlace({place, origin: BI_ORIGINS.MAP})
    },
    [clickPlace, placesStock],
  )

  const modalTitle = mobile ? getEventTitle(event) : undefined
  const modalSubtitle = mobile ? `${shortStartDate} | ${getFormattedLocation(event)}` : undefined

  return (
    <Modal
      className={classNames({
        [classes.desktopModal]: desktop,
      })}
      closeButtonAsButton={desktop}
      dataHook={DH.SEATINGS_MODAL}
      title={modalTitle}
      subtitle={modalSubtitle}
      withoutPadding={desktop}
    >
      <ErrorModal seatingError={seatingError} reservationError={reservationError} />
      {showPriceOptionsPlace ? (
        isPlaceReservableAsWhole(showPriceOptionsPlace) ? (
          <TablePricingOptionsModal />
        ) : (
          <PricingOptionModal />
        )
      ) : null}
      {loading ? <Loader /> : null}
      {mobile ? (
        <Mobile
          onPlaceClick={onPlaceClick}
          onRemoveClick={onRemoveClick}
          onCheckoutClick={handleCheckout}
          selectSeatForDescription={setShowPlaceDescription}
          onCloseClick={onCloseClick}
        />
      ) : (
        <Desktop
          onPlaceClick={onPlaceClick}
          onRemoveClick={onRemoveClick}
          onCheckoutClick={handleCheckout}
          selectSeatForDescription={setShowPlaceDescription}
          showAccessibilityMode={showAccessibilityMode}
        />
      )}
    </Modal>
  )
}
